/**
 * 时间的三层约定，任何时间字段都照这个走：
 *
 * | 层 | 形态 |
 * |---|---|
 * | 存储 | UTC 毫秒整数（`*_at_ms`） |
 * | 传输 | 同上，纯数字——不传 ISO 字符串，它带不带偏移量各端理解不一 |
 * | 展示 / 编辑 | 按**租户时区**，不是浏览器时区 |
 *
 * 为什么不用浏览器时区：运营可能在上海配日本区的活动。按浏览器渲染会让
 * 「0 点开始」配的时候看着对、上线后差几小时——而且两个人在不同地方看同一条
 * 配置会看到不同的时间，对不上账。
 *
 * 实现上不引第三方库：Intl 已经能做时区换算，多一个 date 库要多背一份 tz 数据。
 */

export const DEFAULT_TZ = "UTC";

/**
 * 浏览器所在时区。**只用作「该租户没配时区」的兜底**——
 * 配了时区的租户仍按租户时区渲染，那条约定的理由见 tz-context.tsx。
 *
 * 退回 UTC 的分支给的是没有 ICU 的运行环境，`timeZone` 在那里是空串。
 */
export const systemTz = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;

const partsOf = (ms: number, tz: string) => {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const { type, value } of fmt.formatToParts(new Date(ms))) p[type] = value;
  if (p.hour === "24") p.hour = "00";
  return p;
};

/** 该时区在某时刻的 UTC 偏移分钟（东为正）。夏令时地区随时刻变化，必须按时刻算 */
export function offsetMinutesAt(tz: string, ms: number): number {
  const p = partsOf(ms, tz);
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
}

/** UTC 毫秒 → 该时区的 `YYYY-MM-DDTHH:mm`，可直接喂给 datetime-local 输入框 */
export function msToLocalInput(ms: number | null, tz: string): string {
  if (ms == null) return "";
  const p = partsOf(ms, tz);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * 该时区的墙钟字符串 → UTC 毫秒。
 *
 * 先按 UTC 解释一次拿到候选偏移，再用该候选时刻的真实偏移修正——
 * 夏令时切换日附近，偏移本身取决于结果时刻，一次换算会差一小时。
 */
export function localInputToMs(v: string, tz: string): number | null {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const naive = Date.UTC(+y, +mo - 1, +d, +h, +mi);
  let ms = naive - offsetMinutesAt(tz, naive) * 60000;
  ms = naive - offsetMinutesAt(tz, ms) * 60000;
  return ms;
}

/** 展示用：`2026-08-14 09:30 JST`。带时区缩写，避免看的人以为是自己那边的时间 */
export function formatAt(ms: number | null, tz: string, opts?: { withZone?: boolean; withSeconds?: boolean }): string {
  if (ms == null) return "—";
  const p = partsOf(ms, tz);
  const base = `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}${opts?.withSeconds ? `:${p.second}` : ""}`;
  if (opts?.withZone === false) return base;
  return `${base} ${zoneAbbr(ms, tz)}`;
}

/** 时区缩写（JST / PDT…）。取不到就退回 UTC±HH:MM */
export function zoneAbbr(ms: number, tz: string): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(new Date(ms))
      .find((x) => x.type === "timeZoneName");
    if (part && !/^GMT[+-]?\d*$/.test(part.value)) return part.value;
  } catch {
    /* 落到下面的偏移写法 */
  }
  const off = offsetMinutesAt(tz, ms);
  const sign = off >= 0 ? "+" : "-";
  const a = Math.abs(off);
  return `UTC${sign}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

export function isValidTz(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
